// ariaVisualDecide.js — WHEN TO SHOW, NOT JUST SAY (Braeden 8/14)
//
//   "she needs to know when to show an action, like pulling up something to
//    show them, if shes generating something showing what shes generating, if
//    shes talking about something shes pulling up a visual to show them what
//    shes talking about, very important for communication"
//
// Three moments deserve a picture, and they are different from each other:
//
//   PULL      he asked to see something → show the actual rows, not a summary
//             of them. "2 unpaid, $970" is a sentence; the two invoices with
//             names and numbers is an answer.
//   BUILDING  she is making something → show it appearing, line by line, so he
//             can stop her before she finishes rather than after.
//   TEACHING  she is explaining something he cannot picture → show the shape
//             of it. Follow-up timing, response-speed decay, what is switched
//             on: all of these are diagrams pretending to be paragraphs.
//
// Deliberately NOT everything. A visual on every turn is wallpaper and stops
// being read. This returns null far more often than it returns a spec.
//
// Client-side on purpose: every endpoint it uses is already mounted, so this
// needs no server change to work.

const API = () => (typeof window !== 'undefined' && window.__ARIA_API_BASE__) || '';

function ownerId() {
  try { return localStorage.getItem('howto_owner_id') || 'owner-default'; } catch (_) { return 'owner-default'; }
}
function authHeaders() {
  let tok = ''; try { tok = localStorage.getItem('aria_session_token') || ''; } catch (_) {}
  return tok ? { Authorization: `Bearer ${tok}` } : undefined;
}

// ── PULL: he wants the actual rows on screen ──────────────────────────────
const PULLS = [
  {
    id: 'unpaid', title: 'Money out there',
    re: /\b(who (owes|hasn'?t paid)|unpaid|outstanding|overdue|past due|owes me|money out there)\b/i,
    async load() {
      const d = await fetch(`${API()}/api/bizops/docs?ownerId=${encodeURIComponent(ownerId())}&type=invoice`, { headers: authHeaders() }).then((r) => r.json());
      const open = (d.docs || []).filter((x) => x.status === 'sent' || x.status === 'overdue');
      return { kind: 'table', title: 'Money out there',
        columns: ['Customer', 'Invoice', 'Amount', 'Status'],
        rows: open.map((x) => [x.customerName || '—', x.number || '—', '$' + (x.total || 0), x.status]),
        footer: open.length ? `${open.length} unpaid · $${open.reduce((s, x) => s + Number(x.total || 0), 0)}` : 'Nothing unpaid.' };
    },
  },
  {
    id: 'drafts', title: 'Never sent',
    re: /\b(drafts?|not sent|never sent|unsent|sitting in drafts)\b/i,
    async load() {
      const d = await fetch(`${API()}/api/bizops/docs?ownerId=${encodeURIComponent(ownerId())}&type=invoice`, { headers: authHeaders() }).then((r) => r.json());
      const dr = (d.docs || []).filter((x) => x.status === 'draft');
      return { kind: 'table', title: 'Invoices you never sent',
        columns: ['Customer', 'Invoice', 'Amount'],
        rows: dr.map((x) => [x.customerName || '—', x.number || '—', '$' + (x.total || 0)]),
        footer: dr.length ? `$${dr.reduce((s, x) => s + Number(x.total || 0), 0)} sitting in drafts` : 'Nothing in drafts.' };
    },
  },
  {
    id: 'today', title: "Today",
    re: /\b(todays?|today'?s)\b.{0,12}\b(jobs?|schedule|route|board)\b|\bwhats on today\b|\bwhat do i have (on )?today\b/i,
    async load() {
      const d = await fetch(`${API()}/api/bizops/day?ownerId=${encodeURIComponent(ownerId())}`, { headers: authHeaders() }).then((r) => r.json());
      const jobs = d.jobs || [];
      return { kind: 'table', title: "Today's jobs",
        columns: ['Time', 'Customer', 'Job', 'Status'],
        rows: jobs.map((j) => [j.window || '—', j.customerName || '—', j.service || '—', j.status || '—']),
        footer: jobs.length ? `${jobs.length} booked` : 'Nothing booked today.' };
    },
  },
  {
    id: 'money', title: 'The money',
    re: /\b(hows? the money|this month'?s? (money|numbers)|profit|p ?& ?l|how much did i (make|bank))\b/i,
    async load() {
      const d = await fetch(`${API()}/api/bizops/pnl?ownerId=${encodeURIComponent(ownerId())}`, { headers: authHeaders() }).then((r) => r.json());
      return { kind: 'stats', title: 'This month',
        stats: [
          { label: 'came in', value: '$' + (d.revenue || 0), tone: 'in' },
          { label: 'went out', value: '$' + (d.totalExpenses || 0), tone: 'out' },
          { label: 'profit', value: '$' + (d.profit || 0), tone: (d.profit || 0) >= 0 ? 'in' : 'out' },
          { label: 'paid invoices', value: String(d.paidInvoices || 0) },
        ] };
    },
  },
  {
    id: 'automations', title: 'What runs itself',
    re: /\b(what (can you|do you) automate|whats automated|what runs itself|what are you doing for me|what'?s switched on|what can you actually do)\b/i,
    async load() {
      const d = await fetch(`${API()}/api/automations/status`).then((r) => r.json());
      const a = d.automations || {};
      const rows = Object.entries(a).map(([k, v]) => [k.replace(/_/g, ' '), v.ready ? 'on' : (v.needs || 'off')]);
      return { kind: 'toggles', title: 'What runs itself',
        rows, footer: `${d.readyCount || 0} of ${d.total || rows.length} running` };
    },
  },
];

// ── TEACHING: things he cannot picture from a sentence ────────────────────
const DIAGRAMS = [
  {
    id: 'response_speed',
    re: /\b(how (quick|fast) (do i|should i) (reply|respond)|response time|reply fast|speed of reply|why do i get calls but no booking)\b/i,
    spec: { kind: 'bars', title: 'Why speed decides it',
      note: 'The same lead, answered later.',
      bars: [
        { label: 'under 5 min', value: 100, caption: 'best chance' },
        { label: '30 min', value: 55 },
        { label: '2 hours', value: 25 },
        { label: 'next day', value: 8, caption: 'they hired someone else' },
      ] },
  },
  {
    id: 'followup',
    re: /\b(why do people ghost|follow ?ups?|quote (went )?quiet|chase (a )?quote|no reply after (i )?quote)\b/i,
    spec: { kind: 'steps', title: 'What a follow-up actually looks like',
      steps: [
        { when: 'Same day', what: 'Quote lands while the job is still on their mind' },
        { when: '+2 days', what: 'One short text — "any questions on that?"' },
        { when: '+1 week', what: 'Last nudge, then it goes quiet on purpose' },
        { when: '+1 month', what: 'Back in the list for the next slow week' },
      ] },
  },
  {
    id: 'lead_sources',
    re: /\b(which ads are (actually )?working|what.s a lead costing|where.?s the best place to get leads|cost per lead)\b/i,
    spec: { kind: 'steps', title: 'What has to be tracked before this can be answered',
      steps: [
        { when: '1', what: 'Every lead tagged with where it came from' },
        { when: '2', what: 'Every job tagged back to its lead' },
        { when: '3', what: 'Spend per source, per month' },
        { when: '4', what: 'Then cost-per-job is arithmetic, not a guess' },
      ] },
  },
];

/**
 * Decide whether this turn deserves a picture.
 * @returns {null | {kind:'pull'|'teach', id, load?:fn, spec?:object}}
 */
export function decide(userText) {
  const t = String(userText || '');
  if (!t.trim()) return null;
  for (const p of PULLS) if (p.re.test(t)) return { kind: 'pull', id: p.id, load: p.load };
  for (const d of DIAGRAMS) if (d.re.test(t)) return { kind: 'teach', id: d.id, spec: d.spec };
  return null;
}

/** Resolve a decision into a render-ready spec. Never throws. */
export async function resolve(decision) {
  if (!decision) return null;
  if (decision.spec) return decision.spec;
  try { return await decision.load(); } catch (_) { return null; }
}

export const PULL_IDS = PULLS.map((p) => p.id);
export const DIAGRAM_IDS = DIAGRAMS.map((d) => d.id);
